import React, { useState, useEffect } from 'react';
import { Clock, Info, TrendingUp, Users, AlertCircle, CheckCircle, ArrowLeft, Calendar, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';
import ShareButton from './ShareButton';

const DurationCalculator = () => {
  const [inputs, setInputs] = useState({
    weeklyTraffic: '',
    currentRate: '',
    targetRate: '',
    confidenceLevel: 95
  });

  const [result, setResult] = useState(null);
  const [showTooltip, setShowTooltip] = useState('');

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 테스트 기간 계산 함수
  const calculateDuration = () => {
    const weeklyTraffic = parseInt(inputs.weeklyTraffic) || 0;
    const currentRate = parseFloat(inputs.currentRate) / 100;
    const targetRate = parseFloat(inputs.targetRate) / 100;

    if (weeklyTraffic === 0 || !currentRate || !targetRate || targetRate <= currentRate) return null;

    // 샘플 사이즈 계산 (양측 검정)
    const pooledP = (currentRate + targetRate) / 2;
    const effect = Math.abs(targetRate - currentRate);
    const improvement = ((targetRate - currentRate) / currentRate) * 100;

    // Z-score 계산
    const getZScore = (confidenceLevel) => {
      if (confidenceLevel === 99) return 2.58;
      if (confidenceLevel === 95) return 1.96;
      if (confidenceLevel === 90) return 1.65;
      return 1.96;
    };

    const z_alpha = getZScore(inputs.confidenceLevel);
    const z_beta = 0.84; // 80% 검정력

    const n = (2 * pooledP * (1 - pooledP) * Math.pow(z_alpha + z_beta, 2)) / Math.pow(effect, 2);
    const totalSampleSize = Math.ceil(n);
    const sampleSizePerGroup = Math.ceil(totalSampleSize / 2);

    // 기간 계산
    const weeks = Math.ceil(totalSampleSize / weeklyTraffic);
    const months = Math.ceil(weeks / 4);
    const days = weeks * 7;

    // 월간 트래픽 계산
    const monthlyTraffic = weeklyTraffic * 4;

    // 최소 권장 기간 (통계적 안정성을 위해)
    const minRecommendedWeeks = 2;
    const recommendedWeeks = Math.max(weeks, minRecommendedWeeks);

    return {
      requiredSample: totalSampleSize,
      sampleSizePerGroup,
      estimatedWeeks: weeks,
      estimatedMonths: months,
      estimatedDays: days,
      recommendedWeeks,
      weeklyTraffic,
      monthlyTraffic,
      currentRate: currentRate * 100,
      targetRate: targetRate * 100,
      improvement,
      dailyTraffic: Math.round(weeklyTraffic / 7)
    };
  };

  // 실시간 계산
  useEffect(() => {
    const calculated = calculateDuration();
    setResult(calculated);
  }, [inputs]);

  // 입력 검증
  const isValidInput = () => {
    return inputs.weeklyTraffic &&
      inputs.currentRate &&
      inputs.targetRate &&
      parseInt(inputs.weeklyTraffic) > 0 &&
      parseFloat(inputs.currentRate) > 0 &&
      parseFloat(inputs.currentRate) < 100 &&
      parseFloat(inputs.targetRate) > 0 &&
      parseFloat(inputs.targetRate) < 100 &&
      parseFloat(inputs.targetRate) > parseFloat(inputs.currentRate);
  };

  return (
    <div className="bg-white overflow-hidden">
      {/* 배경 장식 요소 */}
      <div className="absolute top-0 right-0 -z-10 opacity-30">
        <svg width="800" height="800" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="400" cy="400" r="400" fill="url(#radialPurple)" fillOpacity="0.3" />
          <defs>
            <radialGradient id="radialPurple" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(400 400) rotate(90) scale(400)">
              <stop stopColor="#7C3AED" />
              <stop offset="1" stopColor="#7C3AED" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      <Header />

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-white -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/tools"
            className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            전체 도구로 돌아가기
          </Link>

          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4 mr-2" />
              테스트 기간 계산기
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-violet-600">최적의</span> 테스트 기간<br />
              정확하게 계산하기
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              트래픽량과 목표 개선율을 바탕으로 통계적으로 유의한 결과를 얻기 위한 최적의 테스트 기간을 계산합니다.<br />
              조기 종료 위험을 방지하고 신뢰할 수 있는 결과를 얻는 데 필요한 시간을 제공합니다.
            </p>
            <div className="flex justify-center mb-8">
              <ShareButton />
            </div>
          </div>
        </div>
      </section>

      {/* 계산기 메인 섹션 */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* 입력 폼 */}
            <div className="bg-white rounded-2xl shadow-lg border border-purple-200 p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">⏱️</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">테스트 조건 입력</h2>
              </div>

              <div className="space-y-6">
                {/* 주간 트래픽 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('weeklyTraffic')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        주간 방문자 수
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'weeklyTraffic' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        일주일 동안의 웹사이트 방문자 수
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="예: 2500"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    value={inputs.weeklyTraffic}
                    onChange={(e) => handleInputChange('weeklyTraffic', e.target.value)}
                  />
                </div>

                {/* 현재 전환율 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('currentRate')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        현재 전환율 (%)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'currentRate' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        현재 웹사이트의 전환율
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="예: 2.5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    value={inputs.currentRate}
                    onChange={(e) => handleInputChange('currentRate', e.target.value)}
                  />
                </div>

                {/* 목표 전환율 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('targetRate')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        목표 전환율 (%)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'targetRate' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        달성하고 싶은 목표 전환율
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="예: 3.5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    value={inputs.targetRate}
                    onChange={(e) => handleInputChange('targetRate', e.target.value)}
                  />
                </div>

                {/* 신뢰도 수준 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    신뢰도 수준
                  </label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    value={inputs.confidenceLevel}
                    onChange={(e) => handleInputChange('confidenceLevel', parseInt(e.target.value))}
                  >
                    <option value={90}>90%</option>
                    <option value={95}>95% (권장)</option>
                    <option value={99}>99%</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 결과 표시 */}
            <div className="space-y-6">
              {/* 계산 결과 카드 */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 border-2 border-purple-200">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-purple-200 rounded-xl flex items-center justify-center mr-4">
                    <Calendar className="w-6 h-6 text-purple-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">테스트 기간</h2>
                </div>

                {isValidInput() && result ? (
                  <div className="space-y-6">
                    {/* 주요 결과 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/60 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-purple-600 mb-1">
                          {result.estimatedWeeks}주
                        </div>
                        <div className="text-sm text-gray-600">예상 테스트 기간</div>
                      </div>
                      <div className="bg-white/60 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-purple-600 mb-1">
                          {result.estimatedDays}일
                        </div>
                        <div className="text-sm text-gray-600">총 일수</div>
                      </div>
                    </div>

                    {/* 권장 기간 */}
                    {result.recommendedWeeks > result.estimatedWeeks && (
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                        <div className="flex items-center mb-2">
                          <AlertCircle className="w-5 h-5 text-amber-600 mr-2" />
                          <span className="font-semibold text-amber-800">권장 기간</span>
                        </div>
                        <div className="text-lg font-bold text-amber-800">
                          최소 {result.recommendedWeeks}주 권장
                        </div>
                        <div className="text-sm text-amber-700 mt-1">
                          통계적 안정성을 위해 최소 2주 이상 테스트하는 것이 좋습니다
                        </div>
                      </div>
                    )}

                    {/* 트래픽 정보 */}
                    <div className="bg-white/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">트래픽 정보</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">일일 방문자</span>
                          <span className="font-medium">{result.dailyTraffic.toLocaleString()}명</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">주간 방문자</span>
                          <span className="font-medium">{result.weeklyTraffic.toLocaleString()}명</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">월간 방문자</span>
                          <span className="font-medium">{result.monthlyTraffic.toLocaleString()}명</span>
                        </div>
                      </div>
                    </div>

                    {/* 테스트 조건 요약 */}
                    <div className="bg-white/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">테스트 조건</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">현재 전환율</span>
                          <span className="font-medium">{result.currentRate.toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">목표 전환율</span>
                          <span className="font-medium">{result.targetRate.toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">예상 개선률</span>
                          <span className="font-medium text-green-600">+{result.improvement.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">필요 샘플 수</span>
                          <span className="font-medium">{result.requiredSample.toLocaleString()}명</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {!inputs.weeklyTraffic ? '주간 방문자 수를 입력해주세요' :
                        !inputs.currentRate ? '현재 전환율을 입력해주세요' :
                          !inputs.targetRate ? '목표 전환율을 입력해주세요' :
                            parseFloat(inputs.targetRate) <= parseFloat(inputs.currentRate) ? '목표 전환율은 현재 전환율보다 높아야 합니다' :
                              '올바른 값을 입력해주세요'}
                    </p>
                  </div>
                )}
              </div>

              {/* 해석 가이드 */}
              {isValidInput() && result && (
                <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">결과 해석</h3>
                      <div className="text-sm text-blue-800 space-y-2">
                        <p>
                          • <strong>{result.estimatedWeeks}주 동안</strong> 테스트를 진행해야 통계적으로 유의한 결과를 얻을 수 있습니다.
                        </p>
                        <p>
                          • 주간 {result.weeklyTraffic.toLocaleString()}명의 방문자로 {result.improvement.toFixed(1)}% 개선을 감지할 수 있습니다.
                        </p>
                        {result.estimatedWeeks > 8 && (
                          <p className="text-amber-700">
                            • 테스트 기간이 {result.estimatedWeeks}주로 길어집니다. 트래픽 증가나 더 큰 개선율을 고려해보세요.
                          </p>
                        )}
                        {result.estimatedWeeks < 2 && (
                          <p className="text-green-700">
                            • 테스트 기간이 짧지만, 통계적 안정성을 위해 최소 2주는 진행하는 것을 권장합니다.
                          </p>
                        )}
                        <p>
                          • 조기 종료하지 말고 계획된 기간까지 진행하여 신뢰할 수 있는 결과를 얻으세요.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 다른 도구 추천 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">다음 단계 도구</h2>
            <p className="text-xl text-gray-600">테스트 기간 계산 후 이어서 사용할 수 있는 도구들입니다</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-sm border-2 border-yellow-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💰</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">전환율 개선 효과 계산기</h3>
              </div>
              <p className="text-gray-600 mb-6">개선된 전환율로 예상되는 매출 증가를 계산합니다.</p>
              <Link
                to="/tools/effect"
                className="block w-full bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                효과 계산하기
              </Link>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border-2 border-green-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">✅</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">A/B 테스트 결과 판별기</h3>
              </div>
              <p className="text-gray-600 mb-6">테스트 완료 후 결과가 진짜 효과인지 판별합니다.</p>
              <Link
                to="/tools/significance"
                className="block w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                결과 판별하기
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            지금 바로 전환율 최적화를 시작하세요
          </h2>
          <p className="text-xl mb-10 text-blue-100">
            SWITCHUP으로 직관적이고 편리하게 A/B 테스트를 실행하고 전환율을 개선하세요
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-10">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                { label: "직관적인 인터페이스", description: "복잡한 설정 없이 쉽게 시작" },
                { label: "30일 무료 체험", description: "부담 없이 모든 기능 사용" },
                { label: "즉시 결과 확인", description: "실시간 데이터와 분석 제공" }
              ].map((item, index) => (
                <div key={index}>
                  <div className="text-xl font-bold mb-2">{item.label}</div>
                  <div className="text-blue-100">{item.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/freestart"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
            >
              무료로 시작하기
            </Link>
            <Link
              to="/service/ab-test"
              className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300 font-medium"
            >
              SWITCHUP 서비스 보기
            </Link>
          </div>
        </div>
      </section>

      {/* 카카오 문의하기 버튼 */}
      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}

      <Footer />
    </div>
  );
};

export default DurationCalculator;